<template>
  <div class="contain">
    <!-- 검색 영역 -->
    <div class="search_form" ref="search_form">
      <el-form
        :inline="true"
        :model="searchForm"
        :label-position="`left`"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <el-row>
          <el-col :md="8">
            <el-form-item :label="$t(storeSearchLabel)">
              <el-input v-model="searchForm.storeNm" @keyup.enter.native="searchFirstPage" clearable></el-input>
            </el-form-item>
          </el-col>
          <el-col :md="8">
            <el-form-item :label="$t('사용 가능 여부')">
              <combo
                :data="mainVue.code.useYn"
                :label="$t('전체')"
                :model="searchForm.useYn"
                @change="searchForm.useYn = $event"></combo>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <!-- 버튼 -->
    <div class="buttons" ref="buttons">
      <el-button
        v-if="checkAuthActionAccess('StoreSearch')"
        :size="size"
        @click="searchFirstPage"
        icon="el-icon-search"
        >{{ $t('검색') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess('StoreAdd')"
        :size="size"
        @click="add"
        icon="el-icon-circle-plus-outline"
        :disabled="btn.addDisabled"
        >{{ $t('등록') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess('StoreUp')"
        :size="size"
        @click="up"
        icon="el-icon-edit-outline"
        :disabled="btn.upDisabled"
        >{{ $t('수정') }}</el-button
      >
      <el-button
        v-if="checkAuthActionAccess('StoreDel')"
        :size="size"
        @click="del"
        icon="el-icon-remove-outline"
        :disabled="btn.delDisabled"
        >{{ $t('삭제') }}</el-button
      >
    </div>

    <!-- 리스트 -->
    <el-table
      :height="tableHeight"
      ref="table"
      border
      stripe
      highlight-current-row
      tooltip-effect="dark"
      :data="tableData"
      :size="size"
      :empty-text="$t('데이타 없음')"
      @row-click="handleRowClick"
      @select="handleRowChecked"
      @select-all="handleRowChecked"
      @sort-change="handleSortChange">
      <el-table-column
        :label="totalElements.toString()"
        type="index"
        width="55"
        align="center"
        label-class-name="data_cnt"
        :index="indexMethod"></el-table-column>
      <el-table-column type="selection" width="45" align="center"></el-table-column>
      <!-- 정렬은 **서버**가 한다(`sortable="custom"`) — 페이징된 목록에서 `sortable` 은 「이 페이지 안에서만」이라
           사람이 본 순서와 서버가 고른 한 장이 서로 다른 말을 한다. 열의 `prop` 이 곧 서버로 가는 `sortProp` 이다 -->
      <el-table-column
        :label="$t('매장 ID')"
        prop="storeId"
        min-width="100"
        align="center"
        sortable="custom"></el-table-column>
      <el-table-column
        :label="$t('매장명')"
        prop="storeNmLan"
        min-width="200"
        sortable="custom"
        show-overflow-tooltip></el-table-column>

      <!--
        외부 채널 연동 — 값은 검색 뒤 배치 조회 한 번으로 채운다(부가 조회라 실패하면 「—」).
        열 자체가 `PartnerLinkView` 액션 게이트 안이다: 권한 SQL 미적용 환경에서 403 은 강제 로그아웃이다.
      -->
      <el-table-column
        v-if="checkAuthActionAccess('PartnerLinkView')"
        :label="$t('외부 채널')"
        min-width="210"
        align="center">
        <template slot-scope="scope">
          <!--
            chip 은 **축마다** 하나다 — 콜팝 참여(`participation`)와 채널(`links[]`)의 순서까지
            `partnerChipKeys` 한 곳이 정한다(매장 상세 요약줄과 **같은 함수**). 축을 채널로 고정하면
            참여가 목록에서 통째로 사라지고, 같은 규칙의 사본이 둘이 된다.
          -->
          <div v-for="key in partnerChipKeys(scope.row)" :key="key" class="partner-chip-row">
            <PartnerLinkChip
              :view="partnerLinkViews[scope.row.storeCd]"
              :partner="key"
              @select="openPartnerTab(scope.row)" />
          </div>
          <span v-if="partnerChipKeys(scope.row).length === 0">—</span>
        </template>
      </el-table-column>

      <el-table-column :label="$t('QR코드')" min-width="94" align="center">
        <template slot-scope="scope">
          <el-popover placement="right" trigger="click" @show="showQr(scope.row.storeId)">
            <el-row class="modal-content-qr">
              <el-col :md="12" class="qr-wrapper">
                <div>{{ $t('매장 정보 QR') }}</div>
                <div :id="'storeId_' + scope.row.storeId" style="padding: 20px"></div>
                <div>{{ getStoreInfoQr(scope.row.storeId) }}</div>
              </el-col>
              <el-col :md="12" class="qr-wrapper">
                <div>{{ $t('매장 배달  QR') }}</div>
                <div :id="'storeId_O_' + scope.row.storeId" style="padding: 20px"></div>
                <div>{{ getOutQr(scope.row.storeId) }}</div>
              </el-col>
            </el-row>
            <el-button size="mini" slot="reference">{{ $t('QR보기') }}</el-button>
          </el-popover>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('사용 언어')"
        prop="lanNms"
        min-width="150"
        align="left"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('핸드폰 번호')"
        prop="tel"
        :formatter="util.telCell"
        min-width="130"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('영업시간')"
        prop="openTm"
        min-width="160"
        align="left"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('BreakTime')"
        prop="breakTm"
        min-width="160"
        align="left"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('휴무일')"
        prop="holiday"
        min-width="120"
        align="center"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('웹 판매 방식')"
        prop="webSalesTpNms"
        min-width="140"
        align="center"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('키오스크 판매 방식')"
        prop="appSalesTpNms"
        min-width="140"
        align="center"
        show-overflow-tooltip></el-table-column>
      <!--			<el-table-column :label="$t('배달비용')" 	prop="deliveryCost" min-width="80" align="right" :formatter="mainVue.formatterNum"></el-table-column>-->
      <el-table-column :label="$t('주문 통지')" prop="orderNoti" min-width="80" align="center">
        <template slot-scope="scope">{{ scope.row.orderNoti ? 'O' : '' }}</template>
      </el-table-column>
      <el-table-column :label="$t('통지 간격')" prop="notiTime" min-width="80" align="right">
        <template slot-scope="scope">{{ scope.row.orderNoti ? scope.row.notiTime + ' ' + $t('분') : '' }}</template>
      </el-table-column>
      <el-table-column :label="$t('주방 프린터 연동')" prop="printYn" min-width="130" align="center">
        <template slot-scope="scope">{{ scope.row.printYn ? 'O' : '' }}</template>
      </el-table-column>
      <el-table-column :label="$t('국제 결제 사용여부')" prop="internationalPayYn" min-width="130" align="center">
        <template slot-scope="scope">{{ scope.row.internationalPayYn ? 'O' : '' }}</template>
      </el-table-column>
      <el-table-column :label="$t('매장 주소')" prop="storeAddrLan" min-width="200" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.storeAddrLan + ' ' + scope.row.storeAddrDtlLan }}</template>
      </el-table-column>
      <el-table-column :label="$t('설명')" prop="storeDesc" min-width="300" show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('사업자 등록 번호')"
        prop="busiNum"
        :formatter="bizNoFormatter"
        min-width="140"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('팩스 번호')"
        prop="fax"
        :formatter="util.telCell"
        min-width="130"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('대표자명')"
        prop="ceoNm"
        min-width="100"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('가입일')"
        prop="startDt"
        min-width="100"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('탈퇴일')"
        prop="endDt"
        min-width="100"
        align="center"
        sortable="custom"
        show-overflow-tooltip></el-table-column>
      <el-table-column
        :label="$t('사용 가능 여부')"
        prop="useYn"
        min-width="110"
        align="center"
        :formatter="mainVue.formatterCode"></el-table-column>
      <el-table-column :label="$t('우편 번호')" prop="zipCd" min-width="80" align="center"></el-table-column>
      <el-table-column :label="$t('주소')" prop="addr" min-width="300" show-overflow-tooltip></el-table-column>
      <el-table-column :label="$t('상세 주소')" prop="addrDtl" min-width="150" show-overflow-tooltip></el-table-column>
      <el-table-column label="MID" prop="mid" min-width="100" show-overflow-tooltip></el-table-column>
      <!-- @TODO: 확인필요 -->
      <!-- <el-table-column th:label="MID" prop="mid" min-width="100" show-overflow-tooltip></el-table-column> -->
    </el-table>
    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>

    <!-- 입력 팝업 -->
    <el-dialog :title="inputForm.title" :visible.sync="dialogFormVisible" fullscreen>
      <div style="margin-bottom: 10px; margin-left: 10px">
        {{ $t('* 언어 변경후 카테고리, 재료, 옵션 그룹, 옵션, 할인, 메뉴 데이타도 같이 수정 해주셔야 합니다.') }}
      </div>

      <!--
        관리점·운영사의 대행(R-E2) — 서버가 상세에 `lockedFields` 를 실었을 때만 선다(`utils/storeOps`). 잠긴 칸은
        패널들이 읽기 전용으로 그리고, 저장해도 서버가 현재 값으로 되돌린다. 사이드 메뉴로 닿지 않는 그 매장의
        할인 화면은 띠의 바로가기가 연다(`storeProxyDiscountRoute`). 가는 것은 이 창을 닫는 것이라 닫기 버튼과 같은
        확인을 지난다(`askClose`) — `router-link` 는 그 확인을 건너뛰고 저장 안 한 입력을 버렸다(R-J)
      -->
      <el-alert
        v-if="proxyBanner"
        class="store-proxy-banner"
        :title="proxyBanner"
        type="warning"
        :closable="false"
        show-icon>
        <a
          v-if="proxyDiscountRoute"
          :href="$router.resolve(proxyDiscountRoute).href"
          class="store-proxy-banner__link"
          @click.prevent="askClose(proxyDiscountRoute)"
          >{{ $t(storeOpsText.discountShortcut) }}</a
        >
      </el-alert>

      <!-- 주문 접수 임시중단(R-E1) — 저장 버튼과 무관하게 즉시 반영된다(폼 밖). 등록 폼(storeCd 없음)에는 없다 -->
      <StorePauseCard
        v-if="inputForm.storeCd"
        :store-cd="inputForm.storeCd"
        :paused-until.sync="inputForm.pausedUntil" />

      <el-form
        :model="inputForm"
        :label-width="$device.mobile ? `` : `180px`"
        :label-position="$device.mobile ? 'top' : 'left'"
        label-suffix=":"
        :size="size"
        @submit.native.prevent>
        <!--
          매장 폼의 탭은 세 화면이 같은 컴포넌트로 그린다(`StoreFormTabs`) — 이 화면의 탭(샵인샵·외부 채널·TV 쿠폰)만 뒤에
          붙인다. 화면마다 적던 동안 이 다이얼로그에서 「매장 소개·원산지」·「알림톡 설정」이 빠져 있었다(R-S)
        -->
        <StoreFormTabs
          v-model="activiTabStore"
          :form="inputForm"
          :formError="formError"
          :deliveryAgencyOptions="deliveryAgencyOptions"
          :brandCodeOptionsMap="brandCodeOptionsMap"
          @form-error-update="
            ({ key, value }) => {
              formError[key] = value
            }
          "
          @addDeliveryFee="addDeliveryFee"
          @removeDeliveryFee="removeDeliveryFee"
          @addDeliveryAgency="addDeliveryAgency"
          @removeDeliveryAgency="removeDeliveryAgency">
          <el-tab-pane
            v-if="checkAuthActionAccess('ShopInShopView') && inputForm && inputForm.storeCd"
            :label="$t('매장 묶음')"
            name="shopInShop">
            <ShopInShopPanel
              :storeCd="inputForm.storeCd"
              :editable="checkAuthActionAccess('ShopInShopEdit')"
              :attributesEditable="checkAuthActionAccess('StoreGroupAttributesEdit')" />
          </el-tab-pane>

          <el-tab-pane
            v-if="checkAuthActionAccess('PartnerLinkView') && inputForm && inputForm.storeCd"
            :label="$t('외부 채널 연동')"
            name="partner">
            <PartnerLinkPanel
              :storeCd="inputForm.storeCd"
              @updated="partnerLinkUpdated"
              @changed="partnerLinkChanged" />
          </el-tab-pane>

          <!-- 우리동네TV 쿠폰(R-C) — 목록 액션이 있는 세션에만. 탭을 처음 열 때 읽는다(lazy) -->
          <el-tab-pane
            v-if="checkAuthActionAccess(tvCouponListAction) && inputForm && inputForm.storeCd"
            :label="$t(tvCouponTabTitle)"
            name="tvCoupon"
            lazy>
            <TvCouponPanel :storeCd="inputForm.storeCd" />
          </el-tab-pane>
        </StoreFormTabs>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button icon="el-icon-check" :size="size" @click="save" :disabled="btn.saveDisabled">{{
          $t('저장')
        }}</el-button>
        <el-button icon="el-icon-close" :size="size" @click="askClose()">{{ $t('닫기') }}</el-button>
        <el-dialog
          title="Warning"
          :visible.sync="closeDialogVisible"
          width="300px"
          :modal="false"
          :modal-append-to-body="false"
          :append-to-body="true"
          center>
          <span>정말로 페이지를 닫으시겠습니까?</span>
          <span slot="footer" class="dialog-footer">
            <el-button @click="closeDialogVisible = false">아니요</el-button>
            <el-button type="primary" @click="confirmClose">예</el-button>
          </span>
        </el-dialog>
      </div>
    </el-dialog>

    <!--
      목록 chip 의 폴백 진입점 — `up()` 이 상세 다이얼로그를 열지 않는 계정(admin·mng)용이다.
      기존 흐름에 분기를 심지 않고 패널만 단독으로 띄운다. 판정은 `canOpenStoreDialog`(mixin) 하나가 한다.
    -->
    <el-dialog
      :title="partnerDialogTitle"
      :visible.sync="partnerDialogVisible"
      width="720px"
      append-to-body
      destroy-on-close>
      <PartnerLinkPanel
        v-if="partnerDialogStore"
        :storeCd="partnerDialogStore.storeCd"
        @updated="partnerLinkUpdated"
        @changed="partnerLinkChanged" />
    </el-dialog>
  </div>
</template>

<script>
// 대리점
import PartnerLinkChip from '@/components/store/PartnerLinkChip.vue'
import PartnerLinkPanel from '@/components/store/PartnerLinkPanel.vue'
import ShopInShopPanel from '@/components/store/ShopInShopPanel.vue'
import StoreFormTabs from '@/components/store/StoreFormTabs.vue'
import StorePauseCard from '@/components/store/StorePauseCard.vue'
import TvCouponPanel from '@/components/store/TvCouponPanel.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { util } from '@/mixins/utilMixin'
import { bizNoDisplay } from '@/utils/bizNumber'
import { initData } from '@/utils/constants'
import { withPaging } from '@/utils/pagedList'
import { parseStoreCd } from '@/utils/posDeliverySoldout'
import { STORE_FORM_TABS } from '@/utils/storeDetailForm'
import { STORE_OPS_TEXT, canEditStoreDetail, storeProxyBanner, storeProxyDiscountRoute } from '@/utils/storeOps'
import { STORE_ORIGIN } from '@/utils/storeSaveTarget'
import { STORE_SEARCH_PLACEHOLDER } from '@/utils/storeSearchText'
import {
  PARTNER_LINK_API,
  PARTNER_LINK_BATCH_CONFIG,
  PARTNER_LINK_TEXT,
  chunkStoreCds,
  partnerChipKeys,
} from '@/utils/partnerLink'
import { TV_COUPON_ACTION, TV_COUPON_TEXT } from '@/utils/tvCoupon'

import { storeFormMixin } from '@/mixins/storeFormMixin'

export default {
  mixins: [mainVueMixin, pagedListMixin, storeFormMixin],

  components: {
    PartnerLinkChip,
    PartnerLinkPanel,
    ShopInShopPanel,
    StoreFormTabs,
    StorePauseCard,
    TvCouponPanel,
  },

  data: function () {
    var data = initData()
    data.url = '/store'
    data.lans = []
    data.activiTabStore = 'info'
    data.imgList = []
    data.showImgSelect = true
    data.storeTp = []
    data.bankTp = []
    data.menuShowTp = []
    data.globalVatTp = []
    data.globalServiceCostTp = []
    data.deliveryAgencyOptions = []
    data.brandCodeOptionsMap = {}

    // data.searchForm = this.initSearchForm()
    // data.inputForm = this.initInputForm()
    // data.formError = this.initFormError()

    data.closeDialogVisible = false
    /** 닫기 확인의 「예」 뒤에 갈 곳 — 대행 띠의 바로가기가 싣고, 닫기 버튼은 `null`(이 화면에 남는다). [askClose] 만 쓴다 */
    data.closeThenRoute = null

    /** storeCd → `PartnerLinkView`. 배치 조회 결과의 보관처이고, 없는 매장은 「—」다 */
    data.partnerLinkViews = {}
    /** 배치 조회 세대 — 겹친 검색의 늦은 응답이 최신 목록을 덮지 못하게 */
    data.partnerLinkSeq = 0
    /** 폴백 다이얼로그가 보고 있는 목록 행 (`null` 이면 닫혀 있다) */
    data.partnerDialogStore = null
    data.partnerDialogVisible = false
    /** 우리동네TV 쿠폰 탭 — 목록 액션이 있는 세션에만 그린다(없으면 요청이 403 = 로그아웃이다) */
    data.tvCouponListAction = TV_COUPON_ACTION.LIST
    data.tvCouponTabTitle = TV_COUPON_TEXT.tabTitle
    data.storeOpsText = STORE_OPS_TEXT
    /** 검색 칸 이름 — 서버가 한 칸으로 찾는 축(요금·결제·매장 선택 창과 같은 문장, `$t` 키) */
    data.storeSearchLabel = STORE_SEARCH_PLACEHOLDER

    /**
     * 들어오자마자 열 매장(`?storeCd=`) — 관리점·운영사가 관리 매장 목록에서 매장을 누르면 여기로 온다(R-E2).
     * **첫 목록을 읽은 뒤에** 연다: 목록 콜백이 폼을 비우므로 그 전에 열면 늦게 온 목록이 열린 다이얼로그를 덮는다
     */
    data.entryStoreCd = parseStoreCd(this.$route.query.storeCd)

    return data
  },
  computed: {
    /**
     * 이 화면의 다이얼로그는 상세를 읽고 저장하는 수정 폼이다 — 그 둘의 액션을 가진 세션이면 연다(`utils/storeOps`).
     * 역할 이름이 아니라 액션이라 같은 메뉴를 받은 관리점·운영사(SQL 034)도 같은 다이얼로그를 쓴다.
     * 읽기만 가진 내부 관리자는 종전대로 외부 채널 패널만 따로 연다([openPartnerTab]).
     */
    canOpenStoreDialog() {
      return canEditStoreDetail(this.$store.getters['auth/auth'])
    },
    /** 대행 띠 — 대행 계정의 상세(`lockedFields` 있음)일 때만 문장이 있다 */
    proxyBanner() {
      return storeProxyBanner(this.inputForm)
    },
    /** 대행 띠의 「할인 관리」 바로가기 — 그 매장의 할인 화면. 목록 액션이 없는 세션·대행 아닌 상세는 `null` */
    proxyDiscountRoute() {
      return storeProxyDiscountRoute(this.inputForm, this.$store.getters['auth/auth'])
    },
    /**
     * 이 화면의 매장 다이얼로그 탭 — 매장 폼의 탭([STORE_FORM_TABS], `StoreFormTabs` 가 그린다) 뒤에 이 화면이 붙이는 탭이다.
     * 뒤의 셋은 템플릿의 슬롯 `<el-tab-pane name>` 과 같은 목록·같은 순서다
     */
    storeFormTabs() {
      return STORE_FORM_TABS.concat(['shopInShop', 'partner', 'tvCoupon'])
    },
    partnerDialogTitle() {
      const store = this.partnerDialogStore
      const title = this.$t(PARTNER_LINK_TEXT.panelTitle)
      if (!store) return title
      return `${store.storeNmLan || store.storeNm || ''} · ${title}`
    },
  },
  created: function () {
    this.searchForm = this.initSearchForm()
    this.inputForm = this.initInputForm({ statu: 'I' })
    this.formError = this.initFormError()

    this.initLan()
    this.initBankTp()
    this.initMenuShowTp()
    this.initGlobalVatTp()
    this.initGlobalServiceCostTp()
    this.initDeliveryAgencyData()
    this.search()

    setTimeout(
      function () {
        this.$nextTick(
          function () {
            this.mainVue.currVue = this
          }.bind(this)
        )
      }.bind(this),
      500
    )
  },
  watch: {
    'inputForm.startDt': function (newVal) {
      if (!util.isEmpty(this.inputForm.startDt) && !util.isEmpty(this.inputForm.endDt)) {
        if (this.inputForm.startDt > this.inputForm.endDt) {
          this.formError.startDt = this.$t('가입일은 탈퇴일 이전의 일자만 설정 가능 합니다.')
          this.inputForm.startDt = this.inputForm.endDt
          this.formError.endDt = ''
        }
      }
    },
    'inputForm.endDt': function (newVal) {
      if (!util.isEmpty(this.inputForm.startDt) && !util.isEmpty(this.inputForm.endDt)) {
        if (this.inputForm.startDt > this.inputForm.endDt) {
          this.formError.endDt = this.$t('탈퇴일은 가입일 이후의 일자만 설정 가능 합니다.')
          this.formError.startDt = ''
          this.inputForm.endDt = this.inputForm.startDt
        }
      }
    },
  },
  methods: {
    /** 사업자 등록 번호 칸 — 저장 모양(하이픈 유무)과 상관없이 000-00-00000 으로 보인다(`utils/bizNumber`) */
    bizNoFormatter(row) {
      return bizNoDisplay(row.busiNum)
    },
    // 검색 조건 초기화 — 페이징·정렬 축은 [withPaging] 이 붙인다(기본 50건·첫 페이지·서버 기본 정렬)
    initSearchForm: function () {
      return withPaging({
        storeNm: '',
        useYn: 'Y',
      })
    },
    // 그리드 선택 이벤트
    handleRowClick: function (val) {
      if (null != val) {
        this.btn.upDisabled = false
      }
      this.selectData = val
    },
    // 체크 박스 체크 이벤트
    handleRowChecked: function (selection, row) {
      if (selection.length > 0) {
        this.btn.delDisabled = false
      } else {
        this.btn.delDisabled = true
      }

      this.checkedData = []

      for (var i = 0, len = selection.length; i < len; i++) {
        this.checkedData[this.checkedData.length] = selection[i].storeCd
      }
    },
    // 언어 데이타 조회
    initLan: function () {
      this.mainVue.$axios.search(
        '/language',
        null,
        function (result) {
          this.lans = result
        }.bind(this)
      )
    },

    /**
     * 저장 성공 뒤 — 이 화면(사업장 관리자의 「매장 관리」)은 다이얼로그를 닫고 목록을 다시 읽는다.
     *
     * 종전에는 수정 갈래만 `authId === 'custMng'` 을 물어 저장 뒤 할 일이 역할로 갈렸다. 화면이
     * 소유하면 이 화면에 온 모든 역할이 같은 답을 듣는다.
     */
    onStoreSaved: function ({ method }) {
      this.mainVue.$message({ type: 'success', message: this.$t('add' === method ? '등록 성공' : '수정 성공') })
      this.activiTabStore = 'info'
      this.dialogFormVisible = false
      this.search()
    },

    /**
     * 매장 수정 창의 닫기 확인을 여는 **한 곳** — 닫기 버튼도 대행 띠의 바로가기도 여기서 확인을 묻는다(저장 안 한
     * 입력이 버려진다). `route` 가 있으면 「예」 뒤에 그 화면으로 간다([confirmClose]). 확인을 여는 곳이 여기뿐이라
     * 매번 행선지를 새로 적는다 — 「아니요」로 물린 바로가기의 행선지가 다음 닫기에 남지 않는다.
     */
    askClose: function (route = null) {
      this.closeThenRoute = route
      this.closeDialogVisible = true
    },
    /** 닫기 확인의 「예」 — 두 창을 닫고, 바로가기가 실은 행선지가 있으면 간다 */
    confirmClose: function () {
      const route = this.closeThenRoute
      this.closeThenRoute = null
      this.closeDialogVisible = false
      this.dialogFormVisible = false
      if (route) this.$router.push(route).catch(() => {})
    },

    // 추가
    add: function () {
      // 상세를 읽는 중이면 그 응답을 버린다 — 늦게 오면 이 등록 폼을 수정 폼으로 덮는다
      this.cancelStoreDetail()

      this.inputForm = this.initInputForm({ statu: 'I' })
      this.inputForm.title = this.$t('매장 추가')
      this.inputForm.lans = []
      this.inputForm.storeInfos = []
      this.formError = this.initFormError()

      this.imgList = []

      if (null != this.lans && this.lans.length > 0) {
        for (var i = 0, len = this.lans.length; i < len; i++) {
          if (this.lans[i].default_) {
            this.inputForm.defaultLan.id = this.lans[i].id
          }
        }
      }

      this.inputForm.menuShowTp = null != this.menuShowTp && this.menuShowTp.length > 0 ? this.menuShowTp[0] : null

      for (var i = 0, len = this.inputForm.storeTimesDetail.length; i < len; i++) {
        this.formError['storeTimesDetail[' + i + '].tm'] = ''
        this.formError['storeTimesDetail[' + i + '].btm'] = ''
      }

      for (var i = 0, len = this.inputForm.storeTimesSimple.length; i < len; i++) {
        this.formError['storeTimesSimple[' + i + '].tm'] = ''
        this.formError['storeTimesSimple[' + i + '].btm'] = ''
      }

      this.dialogFormVisible = true
      this.setSaveLocked(false)
    },
    // 조회
    search: function () {
      this.mainVue.$axios.search(
        this.url,
        this.searchQuery(),
        function (result) {
          this.applyPage(result)

          this.btn.addDisabled = false
          this.btn.upDisabled = true
          this.btn.delDisabled = true

          this.inputForm = this.initInputForm({ statu: 'I' })
          this.formError = this.initFormError()
          this.imgList = []

          // **이 페이지에 실린 매장만** 배치 조회한다 — 응답이 아니라 `tableData` 를 넘기는 이유는
          // 봉투를 푸는 일을 [applyPage] 한 곳이 이미 했기 때문이다(`result.content` 를 다시 적지 않는다).
          this.loadPartnerLinks(this.tableData)

          this.openEntryStore()
        }.bind(this)
      )
    },
    /**
     * 들어온 매장(`?storeCd=`)의 상세를 한 번 연다 — 목록 행을 누르고 「수정」 한 것과 같은 경로(`up()`)다.
     * 그 매장이 이 페이지에 없어도 연다(사용 여부 필터 밖일 수 있다): 범위는 상세 조회에서 서버가 본다. 서버가 거절하면
     * (범위 밖·없는 매장) 주소가 정한 매장의 안내를, 그 밖의 실패면 일반 오류 안내를 한 번 띄운다([STORE_ORIGIN.ROUTED] —
     * 판정은 `storeReadFailureNotice`, R-K2 J4 · 리뷰 L2).
     */
    openEntryStore() {
      const storeCd = this.entryStoreCd
      if (storeCd == null || !this.canOpenStoreDialog) return

      this.entryStoreCd = null
      this.handleRowClick({ storeCd })
      this.up(undefined, STORE_ORIGIN.ROUTED)
    },
    /**
     * 「외부 채널」 열의 배치 조회 — 검색 결과 한 판에 한 번(100개씩 쪼개서, §3.4 상한).
     *
     * **부가 조회**라 실패한 묶음은 조용히 비운다([PARTNER_LINK_BATCH_CONFIG]): 그 행은 「—」로 남고
     * 목록 자체는 그대로 쓸 수 있다. 권한이 없으면 아예 부르지 않는다 — 403 은 이 앱에서 강제 로그아웃이다.
     */
    async loadPartnerLinks(rows) {
      if (!this.checkAuthActionAccess('PartnerLinkView')) return

      const seq = ++this.partnerLinkSeq
      const chunks = chunkStoreCds((rows || []).map((row) => row.storeCd))
      if (chunks.length === 0) {
        this.partnerLinkViews = {}
        return
      }

      const results = await Promise.all(chunks.map(this.fetchPartnerLinks))
      if (seq !== this.partnerLinkSeq) return // 그 사이 새 검색이 시작됐다 — 이 판은 버린다
      const next = {}
      results.forEach(function (views) {
        views.forEach(function (view) {
          if (view && view.storeCd != null) next[view.storeCd] = view
        })
      })
      this.partnerLinkViews = next
    },
    async fetchPartnerLinks(storeCds) {
      try {
        const res = await this.$axios.get(PARTNER_LINK_API + '/stores', {
          params: { storeCds: storeCds.join(',') },
          ...PARTNER_LINK_BATCH_CONFIG,
        })
        return res.data || []
      } catch (e) {
        // 스코프 밖 매장은 애초에 결과에서 빠지고(403 아님), 그 밖의 실패는 이 열만 「—」로 남는다
        return []
      }
    },
    /**
     * 한 행이 그릴 chip 코드들 — 축과 순서는 `partnerChipKeys` 한 곳이 정한다(매장 상세와 같은 함수).
     * 조회 전·실패는 빈 배열이고 그 칸은 「—」가 된다.
     */
    partnerChipKeys(row) {
      return partnerChipKeys(row ? this.partnerLinkViews[row.storeCd] : null)
    },
    /** 다이얼로그 패널이 뷰를 읽으면 목록 chip 도 같은 값을 본다 (`null` 은 조회 실패 — 목록의 값은 그대로 둔다) */
    partnerLinkUpdated(view) {
      if (!view || view.storeCd == null) return
      this.partnerLinkViews = { ...this.partnerLinkViews, [view.storeCd]: view }
    },
    /**
     * 조회·신청으로 판정이 바뀌었을 수 있다 — 판정의 입자는 사업자번호라(§2.1) 목록에 있는 같은 사업자번호의 형제 행도
     * 다시 읽는다. 응답 객체를 복사하지 않는 이유: 차단 사유(전화 없음 등)는 매장별이다.
     */
    async partnerLinkChanged(view) {
      this.partnerLinkUpdated(view)
      if (!view || !view.bizNo) return
      const seq = this.partnerLinkSeq
      const siblings = Object.values(this.partnerLinkViews)
        .filter((v) => v && v.bizNo === view.bizNo && v.storeCd !== view.storeCd)
        .map((v) => v.storeCd)
      if (siblings.length === 0) return
      const results = await Promise.all(chunkStoreCds(siblings).map(this.fetchPartnerLinks))
      if (seq !== this.partnerLinkSeq) return
      const next = { ...this.partnerLinkViews }
      results.forEach(function (views) {
        views.forEach(function (v) {
          if (v && v.storeCd != null) next[v.storeCd] = v
        })
      })
      this.partnerLinkViews = next
    },
    /**
     * 목록 chip 클릭 — 그 행의 상세 다이얼로그를 열고 「외부 채널 연동」 탭을 편다.
     *
     * `up()` 이 다이얼로그까지 여는 계정(`canOpenStoreDialog`)만 그 경로를 탄다. 그 밖(admin·mng)에서는
     * `up()` 이 폼만 채우고 끝나므로 패널 하나짜리 폴백 다이얼로그를 연다 — 기존 흐름은 그대로 둔다.
     */
    openPartnerTab(row) {
      if (this.canOpenStoreDialog) {
        this.handleRowClick(row)
        this.up('partner') // 조회가 성공해 다이얼로그를 여는 자리에서 탭을 정한다 — 실패하면 아무것도 남지 않는다
        return
      }
      this.partnerDialogStore = row
      this.partnerDialogVisible = true
    },
    // 삭제
    del: function () {
      this.mainVue
        .$confirm(this.$t('매장 정보를 삭제 하시겠습니까?'), this.mainVue.appMsg.systemAlterTitle, {
          confirmButtonText: this.$t('삭제'),
          cancelButtonText: this.$t('취소'),
          type: 'info',
        })
        .then(
          function () {
            this.mainVue.$axios.del(
              this.url + '/' + this.checkedData.join(','),
              function (result) {
                this.mainVue.$message({ type: 'success', message: this.$t('삭제 성공') })
                this.search()
              }.bind(this)
            )
          }.bind(this)
        )
        .catch(
          function () {
            this.mainVue.$message({ type: 'info', message: this.$t('삭제 취소') })
          }.bind(this)
        )
    },
  },
}
</script>

<style scoped>
.el-upload-list__item {
  -webkit-transition: none;
  transition: none;
}

/* 대행 띠 — 임시중단 카드·탭 위 */
.store-proxy-banner {
  margin-bottom: 10px;
}

.store-proxy-banner__link {
  font-weight: bold;
  text-decoration: underline;
}

/* 외부 채널 chip — 채널마다 한 줄. 두 채널이 한 줄에 붙으면 상태 낱말이 서로의 것처럼 읽힌다 */
.partner-chip-row {
  line-height: 1.8;
}

.file_name .el-textarea__inner {
  height: 90px;
}

.modal-content-qr {
  width: 500px;

  text-align: center;
}
@media (max-width: 768px) {
  .modal-content-qr {
    width: 100%;
  }

  .modal-content-qr .el-col + .el-col {
    margin-top: 30px;
  }
}

.qr-wrapper {
  padding: 10px;
}
</style>
